<template>
  <div class="order-confirmation-page">
    <!-- Header -->
    <header class="checkout-header">
      <img
        src="~@/assets/img/photos/product_logo.png"
        alt="商標"
        class="logo"
      />
    </header>

    <!-- 步驟指引 -->
    <div class="step-indicator">
      <span :class="{ active: currentStep === 1 }">1. 填寫資料</span>
      <span :class="{ active: currentStep === 2 }">2. 訂單確認</span>
      <span
        :class="{
          active: currentStep === 3 || currentStep === 4 || currentStep === 5
        }"
      >
        3. 簽約付款
      </span>
    </div>

    <!-- Step 2: 確認訂單資料 -->
    <div v-if="currentStep === 2" class="order-confirmation-section">
      <!-- 購物內容 -->
      <div class="shopping-content" v-if="orderDetails.cart_details">
        <div class="shopping-header">購物內容</div>

        <!-- 方案資訊 -->
        <div
          v-if="
            orderDetails.cart_details.programs &&
              orderDetails.cart_details.programs.length > 0
          "
        >
          <div
            v-for="item in orderDetails.cart_details.programs"
            :key="item.program_id"
            class="product-info"
          >
            <div class="product-name">{{ item.name }}</div>
            <div class="product-details">
              <p>數量：{{ item.count }}</p>
              <p>金額：NT ${{ item.price }}</p>
            </div>
          </div>
        </div>

        <!-- 商品資訊 -->
        <div
          v-if="
            orderDetails.cart_details.products &&
              orderDetails.cart_details.products.length > 0
          "
        >
          <div
            v-for="item in orderDetails.cart_details.products"
            :key="item.product_id"
            class="product-info"
          >
            <div class="product-name">{{ item.name }}</div>
            <div class="product-details">
              <p>數量：{{ item.count }}</p>
              <p>金額：NT ${{ item.price }}</p>
            </div>
          </div>
        </div>

        <!-- 折扣資訊 -->
        <div v-if="orderDetails.type === 0">
          <!-- 會員等級折扣顯示 -->
          <div
            v-if="
              orderDetails.cart_details &&
                orderDetails.cart_details.level_discount_amount > 0
            "
            class="discount-code"
          >
            <span>{{ orderDetails.member.level.name }} 會員等級折扣</span>
            <span>
              - NT ${{ orderDetails.cart_details.level_discount_amount }}
            </span>
          </div>

          <!-- 折扣碼顯示 -->
          <div
            v-if="
              orderDetails.cart_details &&
                orderDetails.cart_details.discount_code_amount > 0
            "
            class="discount-code"
          >
            <span>
              {{ orderDetails.discount_code.discount_code_name }} 折扣碼
            </span>
            <span>
              - NT ${{ orderDetails.cart_details.discount_code_amount }}
            </span>
          </div>
        </div>

        <div v-else-if="orderDetails.type === 1" class="discount-code">
          <span>折扣額</span>
          <span>- NT ${{ orderDetails.discount }}</span>
        </div>

        <!-- 金額計算 -->
        <div class="amount-summary">
          <div class="amount-item total">
            <span>合計：</span>
            <span>NT ${{ orderDetails.total }}</span>
          </div>
        </div>
      </div>

      <!-- 會員資料 -->
      <div class="member-section" v-if="orderDetails.member">
        <h3>資料確認</h3>
        <div class="member-info">
          <div class="form-group">
            <label for="member-name">姓名</label>
            <input
              id="member-name"
              type="text"
              :value="orderDetails.member.name"
              readonly
            />
          </div>
          <div class="form-group">
            <label for="member-birthday">生日</label>
            <input
              id="member-birthday"
              type="text"
              :value="orderDetails.member.birthday"
              readonly
            />
          </div>
          <div class="form-group">
            <label for="member-phone">手機</label>
            <input
              id="member-phone"
              type="text"
              :value="orderDetails.member.phone"
              readonly
            />
          </div>
          <div class="form-group">
            <label for="member-email">Email</label>
            <input
              id="member-email"
              type="email"
              :value="orderDetails.member.email"
              readonly
            />
          </div>
          <div
            class="form-group"
            v-if="orderDetails.member && orderDetails.member.level"
          >
            <label for="member-level">會員等級</label>
            <input
              id="member-level"
              type="text"
              :value="orderDetails.member.level.name"
              readonly
            />
          </div>
        </div>
      </div>

      <!-- 分店選擇 -->
      <div v-if="showBranchSelection" class="payment-method-section">
        <h3>選擇分店</h3>
        <select v-model="selectedBranchId">
          <option
            v-for="branch in branchOptions"
            :key="branch.value"
            :value="branch.value"
            :disabled="branch.value === null"
          >
            {{ branch.label }}
          </option>
        </select>
      </div>

      <!-- 付款方式 -->
      <div class="payment-method-section">
        <h3>付款方式</h3>
        <select v-model="selectedPaymentMethod" @change="handlePaymentChange">
          <option
            v-for="method in paymentMethods"
            :key="method.value"
            :value="method.value"
            :disabled="method.value === null"
          >
            {{ method.label }}
          </option>
        </select>
        <div v-if="showInstallmentOptions" class="installment-options">
          <label>分期期數：</label>
          <select v-model="selectedInstallmentPeriod">
            <option
              v-for="period in installmentPeriods"
              :key="period"
              :value="period"
            >
              {{ period }} 期
            </option>
          </select>
        </div>
      </div>

      <!-- 發票方式 -->
      <div class="invoice-method-section">
        <h3>發票方式</h3>
        <select v-model="selectedInvoiceMethod" @change="handleInvoiceChange">
          <option value="2">電子發票</option>
          <option value="0">手機載具</option>
          <option value="3">統一編號</option>
          <option value="1">自然人憑證載具</option>
        </select>
        <div v-if="showInvoiceInput" class="invoice-input">
          <input
            type="text"
            v-model="invoiceInputValue"
            placeholder="請輸入對應資訊"
          />
        </div>
      </div>

      <!-- 確認按鈕 -->
      <div class="action-buttons">
        <button @click="confirmOrder">確認訂單</button>
      </div>
    </div>

    <!-- Step 3: 顯示合約 PDF -->
    <div v-if="currentStep === 3" class="contract-section">
      <!-- 合約 PDF -->
      <div class="pdf-container" ref="pdfContainer"></div>

      <!-- 同意按鈕 -->
      <div class="action-buttons">
        <button
          class="agree-button"
          :disabled="!canSignContract"
          @click="proceedToSignature"
        >
          同意以上內容並簽署合約
        </button>
      </div>
    </div>

    <!-- Step 4: 簽名與確認 -->
    <div v-if="currentStep === 4" class="signature-section">
      <!-- 簽名板 -->
      <div id="signature-container">
        <canvas ref="signaturePadCanvas" class="signature-canvas"></canvas>
      </div>

      <!-- 按鈕 -->
      <div class="action-buttons">
        <button @click="clearSignature">重簽</button>
        <button @click="confirmSignature">確認</button>
      </div>
    </div>

    <!-- Step 5: 確認已填充 PDF -->
    <div v-if="currentStep === 5" class="filled-contract-section">
      <!-- 填充後的 PDF -->
      <div class="pdf-container" ref="pdfContainer"></div>

      <!-- 按鈕 -->
      <div class="action-buttons">
        <button @click="goBackToContract" :disabled="!isPdfRendered">
          修改
        </button>
        <button @click="submitOrder" :disabled="!isPdfRendered">
          送出並付款
        </button>
      </div>
    </div>

    <div>
      <MessageModal ref="messageModal" />
    </div>
  </div>
</template>

<script>
import {
  fetchOrderDetails,
  fetchPaymentMethods,
  fetchBranchOptions,
  getContract,
  fillContract,
  resetContract,
  submitOrderApi
} from '@/api/order_confirmation';
import MessageModal from '@/components/Modal/MessageModal';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import SignaturePad from 'signature_pad';

export default {
  components: { MessageModal },
  data() {
    return {
      currentStep: 2,
      contractPdfUrl: '',
      canSignContract: false,
      pdfDocument: null, // PDF 文件對象
      currentPage: 1, // 當前顯示的頁面
      totalPages: 0, // PDF 總頁數
      signaturePad: null,
      isPdfRendered: false,
      orderDetails: {
        cart_details: {
          programs: [],
          products: []
        },
        member: {},
        discount_code: null
      },
      paymentMethods: [],
      selectedPaymentMethod: null,
      showInstallmentOptions: false,
      selectedInstallmentPeriod: null,
      installmentPeriods: [3, 6],
      selectedInvoiceMethod: '2',
      showInvoiceInput: false,
      invoiceInputValue: '',
      branchOptions: [],
      selectedBranchId: null,
      showBranchSelection: false
    };
  },
  methods: {
    async loadOrderDetails() {
      const token = this.$route.params.token;

      try {
        const response = await fetchOrderDetails(token);

        // 取得訂單資料
        this.orderDetails = response;

        // 檢查是否需要顯示分店選擇（只有線上購買且有方案時才需要選擇分店）
        this.checkBranchSelectionVisibility();
      } catch (error) {
        this.showError(error);

        // 返回首頁列表
        setTimeout(() => {
          this.$router.push({ name: 'ProductPlan' });
        }, 2000);
        return;
      }
    },
    async loadPaymentMethods() {
      try {
        const methods = await fetchPaymentMethods();
        this.paymentMethods = [
          { value: null, label: '請選擇付款方式' },
          ...methods
        ];
      } catch (error) {
        this.showError('無法載入付款方式');
      }
    },
    async loadBranchOptions() {
      try {
        const branches = await fetchBranchOptions();
        this.branchOptions = [
          { value: null, label: '請選擇分店' },
          ...branches
        ];
      } catch (error) {
        this.showError('無法載入分店選項');
      }
    },
    checkBranchSelectionVisibility() {
      // 檢查是否為線上購買（type = 0 為線上）
      const isOnlinePurchase = this.orderDetails.type === 0;
      // 檢查是否有方案且非視訊方案
      const hasPrograms = this.orderDetails.cart_details?.programs?.length > 0;
      // 檢查是否為視訊方案
      const isVideoProgram = this.orderDetails.is_video_program;

      if (isOnlinePurchase && hasPrograms && !isVideoProgram) {
        // 如果是線上購買、有方案且不是視訊方案，則需要讓用戶選擇分店
        this.showBranchSelection = true;
        this.loadBranchOptions();
      }
    },
    handlePaymentChange() {
      this.showInstallmentOptions = this.selectedPaymentMethod === 6;
    },
    handleInvoiceChange() {
      this.showInvoiceInput =
        this.selectedInvoiceMethod === '0' ||
        this.selectedInvoiceMethod === '3' ||
        this.selectedInvoiceMethod === '1';
    },
    async confirmOrder() {
      // 檢查 email 是否為空
      if (!this.orderDetails.member.email) {
        this.showError('請至 APP 或找服務人員設定 Email，以便接收訂單相關資訊');
        return;
      }

      // 檢查付款方式是否選擇
      if (!this.selectedPaymentMethod) {
        this.showError('請選擇付款方式');
        return;
      }

      // 檢查分期付款時，是否選擇分期期數
      if (this.selectedPaymentMethod === 6 && !this.selectedInstallmentPeriod) {
        this.showError('請選擇分期期數');
        return;
      }

      // 檢查是否需要選擇分店
      if (this.showBranchSelection && !this.selectedBranchId) {
        this.showError('請選擇分店');
        return;
      }

      // 檢查發票方式是否選擇
      if (!this.selectedInvoiceMethod) {
        this.showError('請選擇發票方式');
        return;
      }

      // 如果有輸入框的發票方式，檢查格式
      if (this.selectedInvoiceMethod === '0') {
        // 手機載具驗證
        const carrierRegex = /^\/[0-9A-Z.\-+]{7}$/;
        if (!carrierRegex.test(this.invoiceInputValue)) {
          this.showError(
            '手機載具格式不正確，第一碼必為「/」，其餘7碼由數字0～9、大寫英文A～Z與「.」、「-」、「+」組成'
          );
          this.invoiceInputValue = '';
          return;
        }
      } else if (this.selectedInvoiceMethod === '1') {
        // 自然人憑證載具驗證
        const citizenCardRegex = /^[A-Z]{2}[0-9]{14}$/;
        if (!citizenCardRegex.test(this.invoiceInputValue)) {
          this.showError(
            '自然人憑證載具格式不正確，總長度為16碼，前兩碼為大寫英文A-Z，後14碼為數字0-9'
          );
          this.invoiceInputValue = '';
          return;
        }
      } else if (this.selectedInvoiceMethod === '3') {
        // 統一編號驗證
        const taxIdRegex = /^[0-9]{8}$/;
        if (!taxIdRegex.test(this.invoiceInputValue)) {
          this.showError('統一編號格式不正確，必須是8個數字');
          this.invoiceInputValue = '';
          return;
        }
      } else if (
        (this.selectedInvoiceMethod === '0' ||
          this.selectedInvoiceMethod === '3' ||
          this.selectedInvoiceMethod === '1') &&
        !this.invoiceInputValue
      ) {
        this.showError('請輸入對應的發票資訊');
        return;
      }

      try {
        if (this.orderDetails.has_program == 1) {
          // 含方案則走簽訂合約流程
          const response = await getContract(this.$route.params.token);
          // 獲取合約 PDF 的 URL
          this.contractPdfUrl = response.contract_url;

          this.currentStep = 3;
          this.loadPdf();
        } else {
          // 不含方案直接進金流
          const orderData = {
            token: this.$route.params.token,
            payment_method: this.selectedPaymentMethod,
            installment_period: this.selectedInstallmentPeriod || null,
            invoice_method: this.selectedInvoiceMethod,
            invoice_input: this.invoiceInputValue || null,
            selected_branch_id: this.selectedBranchId || null
          };

          const response = await submitOrderApi(orderData);

          if (response) {
            // 成功返回訂單編號，跳轉到金流 API
            const paymentRedirectUrl = `${process.env.VUE_APP_API_URL}/shopping/redirect-to-payment?order_no=${response.order_no}`;
            window.location.href = paymentRedirectUrl;
          }
        }
      } catch (error) {
        this.showError(error);

        // 返回首頁列表
        setTimeout(() => {
          this.$router.push({ name: 'ProductPlan' });
        }, 2000);
        return;
      }
    },
    showError(message) {
      this.$refs.messageModal.show(message, '錯誤', 'error');
    },
    showSuccess(message) {
      this.$refs.messageModal.show(message, '成功', 'success');
    },
    // 檢測 PDF 是否滾動到底部
    handleScrollPdf() {
      const container = this.$refs.pdfContainer;

      if (!container) {
        // 如果容器不存在，直接返回
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const bottomVisible = containerRect.bottom <= window.innerHeight;

      // 如果 PDF 的底部出現在可視範圍內，啟用按鈕
      this.canSignContract = bottomVisible;
    },
    // 進入簽名流程
    proceedToSignature() {
      if (this.signaturePad) {
        this.signaturePad.clear(); // 清空舊簽名
      }

      window.removeEventListener('scroll', this.handleScrollPdf); // 清除滾動事件

      // 清空 PDF 容器
      const container = this.$refs.pdfContainer;
      if (container) {
        container.innerHTML = '';
      }
      this.currentStep = 4;
      this.initializeSignaturePad();
      //   const canvas = document.getElementById('signature-pad');
      //   this.signaturePad = new SignaturePad(canvas);
    },
    // 返回查看合約
    async goBackToContract() {
      try {
        // 重整 PDF 回未簽名狀態
        const response = await resetContract({
          token: this.$route.params.token
        });

        if (response?.contract_url) {
          // 更新 PDF URL 為重整後的初始合約
          this.contractPdfUrl = response.contract_url;

          // 重新加載 PDF
          await this.loadPdf();

          // 切換回 step 3
          this.currentStep = 3;

          // 重新添加滾動檢測
          window.addEventListener('scroll', this.handleScrollPdf);
        } else {
          this.showError('重整合約失敗，請稍後再試');
        }
      } catch (error) {
        this.showError('重整合約失敗，請稍後再試');
      }
    },
    async loadPdf() {
      try {
        // 指定 Worker 的路徑
        pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js';

        const loadingTask = pdfjsLib.getDocument({
          url: this.contractPdfUrl,
          cMapUrl: '/pdfjs/cmaps/',
          cMapPacked: true // 啟用壓縮格式
        });

        this.pdfDocument = await loadingTask.promise;

        // 渲染所有頁面
        await this.renderAllPages();
      } catch (error) {
        console.error('PDF 加載失敗：', error);
      }
    },
    async renderAllPages() {
      try {
        const container = this.$refs.pdfContainer;

        // 清空容器，防止重複渲染
        container.innerHTML = '';

        for (let pageNum = 1; pageNum <= this.pdfDocument.numPages; pageNum++) {
          const page = await this.pdfDocument.getPage(pageNum);

          // 創建 canvas
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          // 設定 viewport
          const containerWidth = container.offsetWidth;
          const viewport = page.getViewport({
            scale: containerWidth / page.view[2]
          });

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          // 設置每頁的樣式，讓它展開佔據全部寬度
          canvas.style.marginBottom = '20px'; // 增加頁面間距
          canvas.style.width = '100%'; // 設置為容器寬度

          // 將 canvas 添加到容器
          container.appendChild(canvas);

          // 渲染頁面
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
        }

        // 重置按鈕為不可點擊
        this.canSignContract = false;
        // 標記 PDF 已渲染完成
        this.isPdfRendered = false;

        // 添加滾動檢測事件（針對網頁的滾動）
        window.addEventListener('scroll', this.handleScrollPdf);
      } catch (error) {
        console.error('PDF 渲染失敗：', error);
      }
    },
    initializeSignaturePad() {
      this.$nextTick(() => {
        const canvas = this.$refs.signaturePadCanvas;

        if (!canvas) {
          console.error('Canvas 元素未找到');
          return;
        }

        /* 只要手指壓在 canvas 上就鎖住整頁滾動 */
        const lockScroll = () => document.body.classList.add('no-scroll');
        const unlockScroll = () => document.body.classList.remove('no-scroll');

        /* 使用 pointer 事件較新，也向下支援 touch 事件 */
        ['pointerdown', 'touchstart'].forEach(evt =>
          canvas.addEventListener(evt, lockScroll, { passive: false })
        );
        ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(evt =>
          canvas.addEventListener(evt, unlockScroll)
        );

        /* 防抖動 + 縮放修正 */
        ['touchstart', 'touchmove'].forEach(evt =>
          canvas.addEventListener(evt, e => e.preventDefault(), {
            passive: false
          })
        );

        // 動態調整 Canvas 尺寸
        const resizeCanvas = () => {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          const context = canvas.getContext('2d');
          context.scale(ratio, ratio);
        };
        resizeCanvas();

        // 初始化 SignaturePad
        this.signaturePad = new SignaturePad(canvas, {
          minWidth: 5,
          maxWidth: 5,
          backgroundColor: 'rgba(255, 255, 255, 1)', // 背景顏色，避免透明背景
          penColor: 'black' // 筆跡顏色
        });

        // 確保簽名板在窗口大小改變時自適應
        window.addEventListener('resize', resizeCanvas);
      });
    },
    clearSignature() {
      if (this.signaturePad) {
        this.signaturePad.clear();
      }
    },
    async confirmSignature() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        this.showError('請簽名後再確認');
        return;
      }

      // 獲取簽名數據
      const signatureData = this.signaturePad.toDataURL();

      try {
        // 合成簽名合約
        const response = await fillContract({
          token: this.$route.params.token,
          signature: signatureData
        });

        if (response?.contract_url) {
          this.contractPdfUrl = response.contract_url;

          // 禁用按鈕，直到 PDF 渲染完成
          this.isPdfRendered = false;

          this.currentStep = 5;
          await this.loadPdf();

          // PDF 渲染完成後啟用按鈕
          this.isPdfRendered = true;
        } else {
          this.showError('合成簽名合約失敗，請稍後再試');
        }
      } catch (error) {
        this.showError('上傳簽名失敗，請稍後再試', error);
      }
    },
    async submitOrder() {
      const orderData = {
        token: this.$route.params.token,
        payment_method: this.selectedPaymentMethod,
        installment_period: this.selectedInstallmentPeriod || null,
        invoice_method: this.selectedInvoiceMethod,
        invoice_input: this.invoiceInputValue || null,
        selected_branch_id: this.selectedBranchId || null
      };

      try {
        const response = await submitOrderApi(orderData);

        if (response) {
          // 成功返回訂單編號，跳轉到金流 API
          const paymentRedirectUrl = `${process.env.VUE_APP_API_URL}/shopping/redirect-to-payment?order_no=${response.order_no}`;
          window.location.href = paymentRedirectUrl;
        } else {
          this.showError(response.message || '訂單提交失敗');
        }
      } catch (error) {
        this.showError(error.message || '提交失敗，請稍後再試');
      }
    }
  },
  mounted() {
    this.loadOrderDetails();
    this.loadPaymentMethods();
    document.title = '昱驊養生瑜珈中心-線上購課';
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/page/OrderConfirmation.scss';
</style>
